import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Helper function to generate the prompt_type
const generatePromptType = (name: string): string => {
  return `pressrelease_${name.toLowerCase().replace(/\s+/g, '-')}`;
};

export interface PressReleasePrompt {
  id: string;
  brand_id: string;
  prompt_type: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

export const usePressReleasePrompts = (brandId: string | null) => {
  const [prompts, setPrompts] = useState<PressReleasePrompt[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchPrompts = async () => {
    if (!brandId) {
      setPrompts([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cm_prompts')
        .select('*')
        .eq('brand_id', brandId)
        .like('prompt_type', 'pressrelease_%')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPrompts((data || []) as PressReleasePrompt[]);
    } catch (error) {
      console.error('Error fetching press release prompts:', error);
      setPrompts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createPrompt = async (promptData: { name: string; prompt: string }): Promise<PressReleasePrompt | null> => {
    if (!brandId) return null;

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('cm_prompts')
        .insert({
          brand_id: brandId,
          prompt_type: generatePromptType(promptData.name),
          name: promptData.name,
          prompt: promptData.prompt,
        })
        .select()
        .single();

      if (error) throw error;

      setPrompts(prev => [data as PressReleasePrompt, ...prev]);
      return data as PressReleasePrompt;
    } catch (error) {
      console.error('Error creating press release prompt:', error);
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const updatePrompt = async (id: string, promptData: { name?: string; prompt?: string }): Promise<PressReleasePrompt | null> => {
    setIsUpdating(true);
    try {
      const updates: any = {
        ...promptData,
        updated_at: new Date().toISOString(),
      };

      if (promptData.name) {
        updates.prompt_type = generatePromptType(promptData.name);
      }

      const { data, error } = await supabase
        .from('cm_prompts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setPrompts(prev => prev.map(p => p.id === id ? (data as PressReleasePrompt) : p));
      return data as PressReleasePrompt;
    } catch (error) {
      console.error('Error updating press release prompt:', error);
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const deletePrompt = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('cm_prompts')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPrompts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting press release prompt:', error);
    }
  };

  const duplicatePrompt = async (id: string): Promise<PressReleasePrompt | null> => {
    const original = prompts.find(p => p.id === id);
    if (!original || !brandId) return null;

    return createPrompt({
      name: `${original.name} (Copy)`,
      prompt: original.prompt,
    });
  };

  const getDefaultPrompt = (): PressReleasePrompt | null => {
    return prompts.length > 0 ? prompts[0] : null;
  };

  useEffect(() => {
    fetchPrompts();
  }, [brandId]);

  return {
    prompts,
    isLoading,
    isCreating,
    isUpdating,
    refetch: fetchPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    duplicatePrompt,
    getDefaultPrompt,
  };
};
